import config from "./config";
import { cachedPost, bustCache } from "./apiCache";

/**
 * ONE notification poll per signed-in member, shared by every bell on the page.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * api/membership-notifications is the single most-called endpoint in the app,
 * and it topped the error rankings for every status code — mostly by being the
 * request that happens to be in flight when anything else goes wrong. Three
 * separate things made it expensive:
 *
 *  1. Every mounted NotificationBell ran its OWN setInterval. MembershipHeader
 *     renders the bell in the desktop bar and again in each phone bar — all
 *     mounted, one shown by CSS — so a page carried two or more independent
 *     pollers. apiCache collapsed them only while their ticks stayed inside its
 *     3s window; one bell re-mounting on an SPA route change desynchronised
 *     them for good, and the traffic doubled permanently.
 *
 *  2. Nothing checked whether anyone was looking. A backgrounded tab — the
 *     normal state of a course tab — polled every 20s for hours.
 *
 *  3. Failure cost the same as success and repeated at the same rate. A member
 *     whose token had expired, or whose community_token no longer resolved,
 *     re-failed every 20 seconds for the life of the tab. The 'Membership not
 *     found' case is the worst of them: it answers HTTP 200 carrying
 *     {error: 1}, so it is not an error to any interceptor, the bell's catch
 *     never runs, and the loop is completely silent.
 *
 * So: one timer per identity, paused while the tab is hidden, backing off when
 * the endpoint is not answering usefully, and slowed right down when the Reverb
 * socket is up and already pushing the same events live.
 *
 * ── And the per-request cost ────────────────────────────────────────────────
 * A background tick asks for `count_only` — one indexed COUNT on the bridge,
 * instead of 30 rows, a batched actor lookup, a hashid per row and a
 * diffForHumans per row, all of which the badge threw away. The full read
 * happens when it is actually needed: the member opens the panel, or a socket
 * event says something changed. See refresh() below.
 */

/** Normal cadence, unchanged from the per-bell interval this replaces. */
export const BASE_MS = 20000;

/**
 * Cadence while the Reverb socket is connected. The socket already delivers
 * message notifications the instant they happen (NotificationBell listens on
 * `member.{id}`), so the poll is only a safety net for the types it does not
 * push, and does not need to be quick.
 */
export const SOCKET_MS = 120000;

/** Ceiling for the failure backoff. */
export const MAX_BACKOFF_MS = 300000;

/** Live pollers, keyed by identity. */
const pollers = new Map();

const isHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

function createPoller(key, token, memberToken) {
  const subscribers = new Set();
  let timer = null;
  let failures = 0;
  let socketConnected = false;
  let inFlight = false;
  let destroyed = false;
  /**
   * Bumped by every local read mutation. A request captures it on the way out
   * and drops its result if it changed while in flight, so a poll that started
   * before "mark as read" cannot land afterwards and un-read it. It lives here
   * rather than in the component so ONE poll is guarded on behalf of every
   * bell sharing it.
   */
  let mutation = 0;
  let last = null;

  const emit = (data) => {
    subscribers.forEach((fn) => {
      try {
        fn(data);
      } catch (e) {
        /* one bad subscriber must not stop the others */
      }
    });
  };

  const nextDelay = () => {
    if (failures > 0) {
      return Math.min(BASE_MS * 2 ** failures, MAX_BACKOFF_MS);
    }
    return socketConnected ? SOCKET_MS : BASE_MS;
  };

  /** Stop polling but stay usable — see the unsubscribe note below. */
  const stop = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    // A hidden tab schedules nothing at all; becoming visible restarts it.
    if (destroyed || isHidden() || subscribers.size === 0) return;
    timer = setTimeout(() => run(), nextDelay());
  };

  /**
   * @param {boolean} force  a deliberate read — panel opened, socket event.
   *   Skips the cache, is allowed to run from a hidden tab, AND asks for the
   *   full list. A background tick does none of those things: it only needs
   *   the badge number, so it sends count_only and the bridge answers from a
   *   single COUNT.
   */
  async function run(force = false) {
    if (destroyed || inFlight) return;
    // Idle: no bell is mounted, so nobody is waiting on the answer. The
    // visibility listener stays attached while idle and would otherwise fetch
    // for a page that has no bell on it.
    if (subscribers.size === 0) return;
    if (isHidden() && !force) return;

    inFlight = true;
    const startedAt = mutation;
    const wantsList = force;

    try {
      const res = await cachedPost(
        `${config.COMMUNITY_API_URL}/membership-notifications`,
        {
          community_token: token,
          member_token: memberToken,
          ...(wantsList ? {} : { count_only: 1 }),
        },
        { ttl: 3000, force }
      );

      if (res.data?.error === 0) {
        failures = 0;
        if (startedAt === mutation) {
          last = {
            // Absent on a count-only reply. Null, not [], so a subscriber can
            // tell "no list in this answer" from "the list is empty" and does
            // not blank a panel it already has rows for.
            notifications: Array.isArray(res.data.notifications)
              ? res.data.notifications
              : null,
            unread: res.data.unread || 0,
          };
          emit(last);
        } else {
          // Voided by a mutation — no data, but the attempt is over.
          emit(null);
        }
      } else {
        // An {error: 1} envelope over HTTP 200 — 'Membership not found', most
        // often. Counted as a failure ON PURPOSE: it is not transient, nothing
        // upstream treats it as an error, and without this it is the one case
        // that polls forever at full rate with no symptom anywhere.
        failures += 1;
        emit(null);
      }
    } catch (e) {
      // Network error, timeout, or a 401 (which the global interceptor is
      // already bouncing to the login page). Back off either way.
      failures += 1;
      emit(null);
    } finally {
      inFlight = false;
      schedule();
    }
  }

  const onVisibility = () => {
    if (isHidden()) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      return;
    }
    // Back in view after an unknown amount of time — read now, don't wait out
    // a full interval showing a stale badge.
    run();
  };

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibility);
  }

  return {
    key,

    /**
     * fn(data) runs on every SETTLED poll: `{notifications, unread}` when the
     * read produced usable data, `null` when it did not — a network error, an
     * {error:1} envelope, or a result voided by a mutation.
     *
     * Null rather than silence on purpose: the bell shows a spinner while
     * `loading && list.length === 0`, so a subscriber that only ever hears
     * about successes leaves that spinner up forever the first time the
     * endpoint fails. Every subscriber needs to know the attempt is over, not
     * just that it worked.
     */
    subscribe(fn) {
      subscribers.add(fn);
      // A late-joining bell gets what is already known immediately, so a second
      // mount never costs a request.
      if (last) fn(last);
      if (subscribers.size === 1) run();
      else schedule();

      return () => {
        subscribers.delete(fn);
        // Go IDLE, not away.
        //
        // This used to destroy() the poller. On a route change React renders
        // the new tree BEFORE it runs the old tree's cleanup, so the incoming
        // bell had already been handed this exact instance — and then the
        // outgoing bell's cleanup killed it underneath. The new bell was left
        // subscribed to a dead poller whose every run() returned early, so the
        // badge still showed the last known count (replayed from `last`) while
        // opening the panel fetched nothing at all.
        //
        // Idling keeps the instance usable: the timer stops, and the next
        // subscribe() starts it again.
        if (subscribers.size === 0) stop();
      };
    },

    /**
     * A full read, now: the panel is opening, or a socket event says something
     * changed. This is the only path that asks the bridge for the list.
     */
    refresh() {
      return run(true);
    },

    /**
     * Announce a local read mutation: invalidate the shared cache entry and
     * void any poll already in flight (see `mutation` above).
     */
    mutate() {
      mutation += 1;
      bustCache("membership-notifications");
    },

    /** Reverb connected/disconnected — changes the cadence, not the behaviour. */
    setSocketConnected(connected) {
      const next = Boolean(connected);
      if (next === socketConnected) return;
      socketConnected = next;
      // Re-time the pending tick against the new cadence rather than letting
      // the old one run out.
      schedule();
    },

    /**
     * Explicit teardown — sign-out, or a test resetting module state. NOT what
     * happens when the last bell unmounts; that only idles the poller, because
     * a remount is far more likely than a real goodbye.
     */
    destroy() {
      destroyed = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      subscribers.clear();
      pollers.delete(key);
    },

    /* test seams */
    _state: () => ({ failures, socketConnected, subscribers: subscribers.size }),
  };
}

/** The shared poller for this identity, created on first use. */
export function getNotificationPoller(token, memberToken) {
  if (!token || !memberToken) return null;
  const key = `${token}|${memberToken}`;
  let p = pollers.get(key);
  if (!p) {
    p = createPoller(key, token, memberToken);
    pollers.set(key, p);
  }
  return p;
}

/** Test helper — drop every poller. */
export function resetNotificationPollers() {
  Array.from(pollers.values()).forEach((p) => p.destroy());
  pollers.clear();
}
